// @flow

import React from 'react';

import { zoomTableActionsConfig } from '../../commons/zoomTable/zoomTableConfig';
import { RenderRetailTableActionButtons } from '../../commons/tableActionButton/tableActionButton';

import {
  getRetailTableCellObject,
  getFormattedDate,
  getFormattedTime,
  getFormattedDateAndTime,
  goToCNTracking,
  paymentTypeMap
} from '../../../services/helpers';

const completed = 'Completed';

const pending = 'Pending';

const getHandoverStatus = handoverTransactionId =>
  handoverTransactionId !== null ? completed : pending;

const getWriteoffTicketId = writeOffTicketId =>
  `${writeOffTicketId ? `Ticket Id: ${writeOffTicketId}` : '--'}`;

export const retailCollectionsSummaryTableConfig = {
  columns: [
    {
      Header: 'CN',
      headerClassName: 'flex-10',
      className: 'table-cell-custom-retail flex-10',
      accessor: 'cnote',
      Cell: (row: Object) =>
        getRetailTableCellObject(
          row.original.cnote,
          row.original.paymentMode,
          row.original,
          goToCNTracking,
          'cn-highlight'
        ), // paymentMode and paymentType are interchanged due to interchanged values from backend
      sortMethod: (a, b) => (a.toLowerCase() > b.toLowerCase() ? 1 : -1)
    },
    {
      Header: 'Client Details',
      headerClassName: 'flex-15',
      className: 'table-cell-custom-retail flex-15',
      accessor: 'clientName',
      Cell: (row: Object) =>
        getRetailTableCellObject(
          row.original.clientName,
          row.original.clientNumber
        ),
      sortMethod: (a, b) => (a.toLowerCase() > b.toLowerCase() ? 1 : -1)
    },
    {
      Header: 'Collection Time',
      headerClassName: 'flex-10',
      className: 'table-cell-custom-retail flex-10',
      accessor: 'collectionTime',
      Cell: (row: Object) =>
        getRetailTableCellObject(
          getFormattedDate(row.original.collectionTime),
          getFormattedTime(row.original.collectionTime)
        ),
      sortMethod: (a, b) => (a > b ? 1 : -1)
    },
    {
      Header: 'Collected By',
      headerClassName: 'flex-20',
      className: 'table-cell-custom-retail flex-20',
      accessor: 'collectUserName',
      show: false,
      Cell: (row: Object) =>
        getRetailTableCellObject(
          row.original.collectUserName,
          row.original.collectUserMobile
        ),
      sortMethod: (a, b) => (a.toLowerCase() > b.toLowerCase() ? 1 : -1)
    },
    {
      headerClassName: 'table-head-custom-retail flex-20',
      Header: () => (
        <div>
          <div>Amount</div>
          <div>Payment Mode</div>
        </div>
      ),
      className: 'table-cell-custom-retail flex-20',
      accessor: 'totalAmount',
      Cell: (row: Object) =>
        getRetailTableCellObject(
          `₹${Math.round(row.original.totalAmount * 100) / 100}${
            row.original.bankName ? ` | ${row.original.bankName}` : ''
          }`,
          `${row.original.paymentType}${
            row.original.paymentType !== paymentTypeMap.CASH.displayValue &&
            row.original.chequeNumber
              ? ` No. ${row.original.chequeNumber}`
              : ''
          } ${
            row.original.bankNameOfBouncedCheque &&
            row.original.chequeNumberOfBouncedCheque
              ? `, Bounced Cheque ${row.original.bankNameOfBouncedCheque}|${row.original.chequeNumberOfBouncedCheque}`
              : ''
          } `
        ),
      sortMethod: (a, b) => (a > b ? 1 : -1)
    },
    {
      headerClassName: 'table-head-custom-retail flex-10',
      Header: () => (
        <div>
          <div>TDS</div>
          <div>Deduction</div>
        </div>
      ),
      className: 'table-cell-custom-retail flex-10',
      accessor: 'tdsPercentage',
      Cell: (row: Object) =>
        getRetailTableCellObject(
          row.original.tdsPercentage &&
            row.original.tdsPercentage.toFixed(3).concat(' %')
        ),
      sortMethod: (a, b) => (a > b ? 1 : -1)
    },
    {
      headerClassName: 'table-head-custom-retail flex-15',
      Header: () => (
        <div>
          <div>Handover Status</div>
          <div>Transaction ID</div>
        </div>
      ),
      className: 'table-cell-custom-retail flex-15',
      accessor: 'handoverTransactionId',
      Cell: (row: Object) =>
        getRetailTableCellObject(
          row.original.handoverStatus !== null
            ? row.original.handoverStatus
            : getHandoverStatus(row.original.handoverTransactionId),
          row.original.handoverTransactionId
        ),
      sortMethod: (a, b) => (a.toLowerCase() > b.toLowerCase() ? 1 : -1)
    },
    {
      Header: 'Handover Details',
      headerClassName: 'flex-15',
      className: 'table-cell-custom-retail flex-15',
      accessor: 'handoverRecieverName',
      Cell: (row: Object) =>
        row.original.handoverTransactionId !== null
          ? getRetailTableCellObject(
              row.original.handoverRecieverName
                ? row.original.handoverRecieverName
                : getWriteoffTicketId(row.original.writeOffTicketId),
              getFormattedDateAndTime(row.original.handoverTime)
            )
          : getRetailTableCellObject(
              getWriteoffTicketId(row.original.writeOffTicketId)
            ),
      sortMethod: (a, b) => (a.toLowerCase() > b.toLowerCase() ? 1 : -1)
    },
    {
      Header: 'Actions',
      headerClassName: 'flex-15',
      className: 'flex-15 rt-td table-cell-custom-retail',
      width: 150,
      maxWidth: 150,
      sortable: false,
      Cell: (obj: Object) =>
        obj.original.handoverTransactionId === null &&
        obj.original.paymentType !== 'Write-Off' &&
        obj.original.completionStatus !== 'INCOMPLETE' ? (
          <RenderRetailTableActionButtons
            rowData={obj.original || {}}
            buttonConfig={
              zoomTableActionsConfig.RTL_COLLECTIONS_SUMMARY.buttons
            }
          />
        ) : !(
            obj.original.handoverTransactionId === null &&
            obj.original.paymentType !== 'Write-Off'
          ) ? null : (
          <div
            class="break-word"
            title="CN completion status is INCOMPLETE. Please complete consignment first to enable editing payment details."
            style={{ color: 'red' }}
          >
            CN completion status is INCOMPLETE. Please complete consignment
            first to enable editing payment details.
          </div>
        )
    }
  ],
  className: 'zoomTableLegacyWrapper',
  noDataText: 'No data found',
  minRows: 7,
  pageSizeOptions: [7, 10, 15, 25, 50, 100],
  style: { maxHeight: 'calc(100vh - 22rem)' }
};

export const retailCollectionsSummaryTableActionsConfig = {
  buttons: [
    {
      label: 'EDIT',
      hide: rowData => {
        let shouldHide = true;
        if (
          zoomTableActionsConfig.RTL_COLLECTIONS_SUMMARY.actions &&
          zoomTableActionsConfig.RTL_COLLECTIONS_SUMMARY.actions
            .isEditAllowedForRetailCollections
        ) {
          shouldHide = !zoomTableActionsConfig.RTL_COLLECTIONS_SUMMARY.actions.isEditAllowedForRetailCollections();
        }
        return shouldHide;
      },
      onClick: rowData => {
        if (
          zoomTableActionsConfig.RTL_COLLECTIONS_SUMMARY.actions &&
          zoomTableActionsConfig.RTL_COLLECTIONS_SUMMARY.actions
            .editPaymentDetails
        ) {
          zoomTableActionsConfig.RTL_COLLECTIONS_SUMMARY.actions.editPaymentDetails(
            rowData
          );
        }
      }
    }
  ]
};
